const _clusterArray = (items: string[], masterData: any): string[] => {
  if (!items || items.length === 0) return [];
  
  let clusters: any[] = [];
  if (Array.isArray(masterData)) {
    clusters = masterData;
  } else if (masterData && Array.isArray(masterData.topIndustryClusters)) {
    clusters = masterData.topIndustryClusters;
  }

  if (clusters.length === 0) return items;

  const result: string[] = [];
  const itemsSet = new Set(items);
  const subsumedItems = new Set<string>();

  clusters.forEach((cluster) => {
    if (cluster && cluster.children && Array.isArray(cluster.children) && cluster.children.length > 0) {
      const clusterChildNames = cluster.children.map((child: any) => child.name);
      const isCompletelySelected = clusterChildNames.every((childName: string) => itemsSet.has(childName));

      if (isCompletelySelected) {
        result.push(cluster.name);
        clusterChildNames.forEach((childName: string) => subsumedItems.add(childName));
      }
    }
  });

  items.forEach((item) => {
    if (!subsumedItems.has(item)) {
      result.push(item);
    }
  });

  return result;
};

const _removeFromArray = (items: string[], removedItem: string, masterData: any): string[] => {
  if (!items || items.length === 0) return [];
  if (!removedItem) return items;

  let clusters: any[] = [];
  if (Array.isArray(masterData)) {
    clusters = masterData;
  } else if (masterData && Array.isArray(masterData.topIndustryClusters)) {
    clusters = masterData.topIndustryClusters;
  }

  if (clusters.length === 0) {
    return items.filter((item) => item !== removedItem);
  }

  const cluster = clusters.find((c: any) => c.name === removedItem);

  if (cluster && cluster.children && Array.isArray(cluster.children)) {
    const clusterChildNamesSet = new Set(cluster.children.map((child: any) => child.name));
    return items.filter((item) => !clusterChildNamesSet.has(item));
  }

  return items.filter((item) => item !== removedItem);
};

/**
 * Processes a full industry object, returning clustered string arrays for industries.
 * @param industryObj - The industry state object containing industries array
 * @param masterData - Master cluster data
 * @returns Object with clustered arrays: { industries: string[] }
 */
export const getClusteredIndustries = (industryObj: any, masterData: any) => {
  if (!industryObj) return { industries: [] };
  
  return {
    industries: _clusterArray(industryObj.industries || [], masterData)
  };
};

/**
 * Removes a clustered or standalone industry item from a full industry state object.
 * 
 * @param industryObj - The industry state object
 * @param removedItem - The string to remove
 * @param masterData - Master cluster data
 * @returns An object containing the fully updated industryObj, and the new clustered string arrays.
 */
export const removeIndustryOrCluster = (
  industryObj: any, 
  removedItem: string, 
  masterData: any
) => {
  const updated = { ...industryObj };
  
  updated.industries = _removeFromArray(updated.industries || [], removedItem, masterData);

  const clusteredStrs = getClusteredIndustries(updated, masterData);
  return {
    updatedIndustries: updated,
    industries: clusteredStrs.industries
  };
};
